import type { BenchmarkSelection, CompanySlug, ModelView } from "../shared/contracts.ts";
import { resolveModelScore } from "../shared/benchmark-observation.ts";
import { effectivePricing, priceBandFor, tierSchemeForVersion, type PriceMode, type PricingActivity, type ScoreMetric } from "./pricing.ts";

export type LandscapePeriod = "all" | "best" | `year:${2023 | 2024 | 2025 | 2026}`;

export const LANDSCAPE_PERIOD_OPTIONS: ReadonlyArray<{ value: LandscapePeriod; label: string; title?: string }> = [
  { value: "all", label: "All" },
  { value: "best", label: "Best", title: "Each lab's best non-dominated model in every price band" },
  { value: "year:2026", label: "2026" },
  { value: "year:2025", label: "2025" },
  { value: "year:2024", label: "2024" },
  { value: "year:2023", label: "2023" },
];

export interface ModelReleaseDate {
  timestamp: number;
  displayValue: string;
  provenance: "Original launch" | "Released" | "Cataloged";
}

/** Returns the mode-specific date used for release ordering and provenance copy. */
export function modelReleaseDate(model: ModelView, priceMode: PriceMode): ModelReleaseDate | null {
  const dateOnly = priceMode === "original" || model.officialReleaseDate !== null;
  const displayValue = priceMode === "original"
    ? model.originalLaunchDate ?? null
    : model.officialReleaseDate ?? model.catalogedAt;
  if (!displayValue) return null;
  let timestamp: number;
  if (dateOnly) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(displayValue);
    if (!match) return null;
    timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (new Date(timestamp).toISOString().slice(0, 10) !== displayValue) return null;
  } else {
    timestamp = new Date(displayValue).getTime();
  }
  if (!Number.isFinite(timestamp)) return null;
  return {
    timestamp,
    displayValue,
    provenance: priceMode === "original" ? "Original launch" : model.officialReleaseDate ? "Released" : "Cataloged",
  };
}

function modelTimestamp(model: ModelView, priceMode: PriceMode): number {
  return modelReleaseDate(model, priceMode)?.timestamp ?? Number.NaN;
}

function isBetterCandidate(candidate: ModelView, incumbent: ModelView, selection: BenchmarkSelection, priceMode: PriceMode, activity: PricingActivity, costTierVersion: string): boolean {
  const candidateScore = resolveModelScore(candidate, selection)?.value ?? Number.NEGATIVE_INFINITY;
  const incumbentScore = resolveModelScore(incumbent, selection)?.value ?? Number.NEGATIVE_INFINITY;
  if (candidateScore !== incumbentScore) return candidateScore > incumbentScore;
  if (selection.source === "artificial-analysis" && candidate.intelligenceIndexEstimated !== incumbent.intelligenceIndexEstimated) return !candidate.intelligenceIndexEstimated;
  const candidatePrice = effectivePricing(candidate, priceMode, activity, costTierVersion).value ?? Number.POSITIVE_INFINITY;
  const incumbentPrice = effectivePricing(incumbent, priceMode, activity, costTierVersion).value ?? Number.POSITIVE_INFINITY;
  if (candidatePrice !== incumbentPrice) return candidatePrice < incumbentPrice;
  const candidateDate = modelTimestamp(candidate, priceMode);
  const incumbentDate = modelTimestamp(incumbent, priceMode);
  if (candidateDate !== incumbentDate) return candidateDate > incumbentDate;
  return candidate.id.localeCompare(incumbent.id) < 0;
}

export function selectLatestScoredPricedModels(
  models: readonly ModelView[],
  priceMode: PriceMode,
  activity: PricingActivity,
  costTierVersion: string,
  limit = 5,
  selection: BenchmarkSelection = { source: "artificial-analysis", metric: "intelligenceIndex" },
): ModelView[] {
  const normalizedLimit = Math.max(0, Math.floor(limit));
  return [...models]
    .filter((model) => resolveModelScore(model, selection)?.estimated === false
      && effectivePricing(model, priceMode, activity, costTierVersion).value !== null
      && modelReleaseDate(model, priceMode) !== null)
    .sort((left, right) => modelTimestamp(right, priceMode) - modelTimestamp(left, priceMode) || left.id.localeCompare(right.id))
    .slice(0, normalizedLimit);
}

export function selectLatestScoredPricedModel(
  models: readonly ModelView[],
  priceMode: PriceMode,
  activity: PricingActivity,
  costTierVersion: string,
  selection: BenchmarkSelection = { source: "artificial-analysis", metric: "intelligenceIndex" },
): ModelView | null {
  return selectLatestScoredPricedModels(models, priceMode, activity, costTierVersion, 1, selection)[0] ?? null;
}

export function filterLandscapeModels(
  models: ModelView[],
  options: {
    company: CompanySlug | "all";
    period: LandscapePeriod;
    metric?: ScoreMetric;
    selection?: BenchmarkSelection;
    priceMode: PriceMode;
    costTierVersion: string;
    activity: PricingActivity;
    showEstimates: boolean;
  },
): ModelView[] {
  const selection = options.selection ?? { source: "artificial-analysis", metric: options.metric ?? "intelligenceIndex" };
  const candidates = models.filter((model) =>
    (options.company === "all" || model.companySlug === options.company)
    && (selection.source === "epoch-eci" || options.showEstimates || !model.intelligenceIndexEstimated));

  if (options.period.startsWith("year:")) {
    const year = Number(options.period.slice("year:".length));
    return candidates.filter((model) => new Date(modelTimestamp(model, options.priceMode)).getUTCFullYear() === year);
  }
  if (options.period !== "best") return candidates;

  const tierScheme = tierSchemeForVersion(options.costTierVersion, options.activity);
  const pricedCandidates = candidates.flatMap((model) => {
    if (resolveModelScore(model, selection) === null) return [];
    const pricing = effectivePricing(model, options.priceMode, options.activity, options.costTierVersion);
    const priceBand = priceBandFor(pricing, tierScheme);
    if (priceBand === "unknown" || pricing.value === null) return [];
    return [{ model, priceBand, tierIndex: tierScheme.order.indexOf(priceBand) }];
  });
  const dominatesEveryMetric = (cheaper: ModelView, expensive: ModelView): boolean => {
    if (selection.source === "epoch-eci") {
      const cheap = resolveModelScore(cheaper, selection), costly = resolveModelScore(expensive, selection);
      return cheap !== null && costly !== null && (cheap.value > costly.value
        || (cheap.value === costly.value && isBetterCandidate(cheaper, expensive, selection, options.priceMode, options.activity, options.costTierVersion)));
    }
    const metrics: ScoreMetric[] = ["intelligenceIndex", "codingIndex", "agenticIndex"];
    return metrics.every((metric) => cheaper[metric] !== null && (expensive[metric] === null || cheaper[metric]! > expensive[metric]!));
  };
  const frontierCandidates = pricedCandidates.filter((candidate) => !pricedCandidates.some((cheaper) =>
    cheaper.model.companySlug === candidate.model.companySlug
    && cheaper.tierIndex < candidate.tierIndex
    && dominatesEveryMetric(cheaper.model, candidate.model)));
  const winners = new Map<string, (typeof pricedCandidates)[number]>();
  for (const candidate of frontierCandidates) {
    const key = `${candidate.model.companySlug}:${candidate.priceBand}`;
    const incumbent = winners.get(key);
    if (!incumbent || isBetterCandidate(candidate.model, incumbent.model, selection, options.priceMode, options.activity, options.costTierVersion)) winners.set(key, candidate);
  }
  const winnerIds = new Set([...winners.values()].map((winner) => winner.model.id));
  return candidates.filter((model) => winnerIds.has(model.id));
}

export interface LandscapeEligibilityOptions {
  company: CompanySlug | "all";
  period: LandscapePeriod;
  query?: string;
  metric?: ScoreMetric;
  selection?: BenchmarkSelection;
  priceMode: PriceMode;
  costTierVersion: string;
  activity: PricingActivity;
  showEstimates: boolean;
}

/**
 * The canonical Artificial Analysis landscape set. The returned source-order
 * list is the only model set consumed by the count, selection, and chart.
 */
export function eligibleLandscapeModels(
  models: readonly ModelView[],
  options: LandscapeEligibilityOptions,
): ModelView[] {
  const seen = new Set<string>();
  const unique = models.filter((model) => {
    if (seen.has(model.id)) return false;
    seen.add(model.id);
    return true;
  });
  const filtered = filterLandscapeModels(unique, options);
  const tierScheme = tierSchemeForVersion(options.costTierVersion, options.activity);
  const searchTerms = options.query?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? [];
  return filtered.filter((model) => {
    const identity = [model.name, model.shortName, model.id, model.canonicalSlug, model.companyName].join(" ").toLowerCase();
    if (!searchTerms.every((term) => identity.includes(term))) return false;
    const selection = options.selection ?? { source: "artificial-analysis", metric: options.metric ?? "intelligenceIndex" };
    if (resolveModelScore(model, selection) === null) return false;
    const pricing = effectivePricing(model, options.priceMode, options.activity, options.costTierVersion);
    return priceBandFor(pricing, tierScheme) !== "unknown";
  });
}
